// apps/pwa-pos/src/lib/auth.ts
// Sprint 33 — Auth minimal sin PowerSync. Mantiene login online + offline PIN
// usando Supabase Auth directo + cache de usuarios en localStorage.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { mapAuthErrorMessage } from './auth-errors';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export interface UserSession {
  id: string;
  email: string;
  nombre?: string;
  rol?: string;
  organization_id?: string;
  sucursal_id?: string;
  isOffline: boolean;
}

const OFFLINE_SESSION_KEY = 'mibodega_offline_session';
const USUARIOS_CACHE_KEY = 'mibodega.usuarios_cache';

interface UsuarioCache {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  pin: string;
  organization_id: string;
  sucursal_id?: string | null;
  cachedAt: string; // ISO timestamp — permite expirar cache de PIN
}

const USUARIOS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

let _client: SupabaseClient | null | undefined;

function getClient(): SupabaseClient | null {
  if (_client !== undefined) return _client;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    _client = null;
    return null;
  }
  _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return _client;
}

export class SupabaseConnector {
  readonly client: SupabaseClient | null;
  private currentUserSession: UserSession | null = null;
  private currentOrgId: string | null = null;

  constructor() {
    this.client = getClient();
    this.loadOfflineSession();
  }

  private loadOfflineSession(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const stored = localStorage.getItem(OFFLINE_SESSION_KEY);
      if (stored) {
        this.currentUserSession = JSON.parse(stored);
        this.currentOrgId = this.currentUserSession?.organization_id ?? null;
      }
    } catch {
      this.currentUserSession = null;
    }
  }

  getCurrentUser(): UserSession | null {
    return this.currentUserSession;
  }

  getCurrentOrgId(): string | null {
    return this.currentOrgId;
  }

  getCurrentSucursalId(): string | null {
    return this.currentUserSession?.sucursal_id ?? null;
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.client) return null;
    const { data } = await this.client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  async loginOnline(email: string, password: string): Promise<UserSession> {
    if (!this.client) throw new Error('Supabase no inicializado. Revisar .env');

    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error || !data.user) {
      const raw = error?.message ?? 'Login falló';
      throw new Error(mapAuthErrorMessage(raw));
    }

    const session: UserSession = {
      id: data.user.id,
      email: data.user.email ?? email,
      isOffline: false,
    };

    this.currentOrgId = null;
    this.currentUserSession = session;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify(session));
    }

    await this.cacheUserProfile(data.user.id, email, password).catch(() => undefined);
    if (!this.currentOrgId) {
      await this.logout();
      throw new Error(mapAuthErrorMessage('Usuario sin ficha'));
    }
    return session;
  }

  private async cacheUserProfile(userId: string, _email: string, _password: string): Promise<void> {
    if (!this.client) return;
    try {
      const { data: usuario } = await this.client
        .from('usuarios')
        .select('id, email, nombre, rol, pin, organization_id, sucursal_id')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (usuario) {
        this.currentOrgId = usuario.organization_id ?? null;
        const prev = this.currentUserSession ?? ({} as UserSession);
        this.currentUserSession = {
          ...prev,
          id: usuario.id,
          email: usuario.email ?? prev.email,
          nombre: usuario.nombre ?? undefined,
          rol: usuario.rol ?? undefined,
          organization_id: usuario.organization_id,
          sucursal_id: usuario.sucursal_id ?? undefined,
        };
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify(this.currentUserSession));

          const usuarios: UsuarioCache[] = JSON.parse(
            localStorage.getItem(USUARIOS_CACHE_KEY) ?? '[]',
          );
          const next: UsuarioCache = {
            id: usuario.id,
            email: usuario.email ?? '',
            nombre: usuario.nombre ?? '',
            rol: usuario.rol ?? '',
            pin: usuario.pin ?? '',
            organization_id: usuario.organization_id,
            sucursal_id: usuario.sucursal_id ?? null,
            cachedAt: new Date().toISOString(),
          };
          const idx = usuarios.findIndex((u) => u.id === next.id);
          if (idx >= 0) usuarios[idx] = next;
          else usuarios.push(next);
          localStorage.setItem(USUARIOS_CACHE_KEY, JSON.stringify(usuarios));
        }
      }
    } catch {
      // ignore — sin offline cache para esta sesion
    }
  }

  async loginOffline(pin: string): Promise<UserSession | null> {
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(USUARIOS_CACHE_KEY);
      if (!raw) return null;
      const usuarios = JSON.parse(raw) as UsuarioCache[];
      const matched = usuarios.find((u) => u.pin === pin || u.pin === String(pin));
      if (!matched) return null;

      // Expirar cache de PIN si tiene más de TTL_MS
      const cachedAt = matched.cachedAt ? new Date(matched.cachedAt).getTime() : 0;
      const now = Date.now();
      if (now - cachedAt > USUARIOS_CACHE_TTL_MS) {
        // Cache vencida: eliminar del cache y rechazar login offline
        const refreshed = usuarios.filter((u) => u.id !== matched.id);
        localStorage.setItem(USUARIOS_CACHE_KEY, JSON.stringify(refreshed));
        console.warn('[SupabaseConnector] PIN cache vencida, requiere login online');
        return null;
      }

      const session: UserSession = {
        id: matched.id,
        email: matched.email,
        nombre: matched.nombre,
        rol: matched.rol,
        organization_id: matched.organization_id,
        sucursal_id: matched.sucursal_id ?? undefined,
        isOffline: true,
      };

      this.currentUserSession = session;
      this.currentOrgId = matched.organization_id ?? null;
      localStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify(session));
      return session;
    } catch (err) {
      console.error('[SupabaseConnector] Error en loginOffline:', err);
      return null;
    }
  }

  async logout(): Promise<void> {
    this.currentUserSession = null;
    this.currentOrgId = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(OFFLINE_SESSION_KEY);
    }
    if (this.client) {
      await this.client.auth.signOut();
    }
  }
}

export const connector = new SupabaseConnector();
